<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAuth, useOrg } from '@/composables';
import {
  isSelectGroupDivider,
  isSelectGroupHeader,
  isSelectGroupItem
} from '@/composables/selectGroupEntries';
import { useEntitySelectModel } from '@/composables/useEntitySelectModel';
import { useLoadTypeSelectItems } from '@/composables/useLoadTypeSelectItems';
import { useLoadTypeSelectList } from '@/composables/useLoadTypeSelectList';

import type { DockDetails } from '@satellite/composables/dockPresenterShared';
import type { LoadTypeDetails } from '@satellite/composables/loadTypePresenterShared';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenter';
import type { EntitySelectModelValue } from '@/composables/useEntitySelectModel';
import type { LoadTypeSelectEntry } from '@/composables/useLoadTypeSelectItems';

defineOptions({ inheritAttrs: false });

const {
  modelValue,
  warehouse,
  docks,
  multiple = false,
  includeOrgLoadTypes = true,
  showOnlyAssignedLoadTypes = false,
  allowCreate = false,
  canCreate = null
} = defineProps<{
  /** One load type, or many when `multiple`. */
  modelValue: EntitySelectModelValue<LoadTypeDetails>;
  /** Scopes the load type query and labels the warehouse group header. */
  warehouse: Pick<WarehouseDetails, 'id' | 'name'>;
  /**
   * Narrows to load types these docks offer; empty, or docks without `loadTypeIds`, do not
   * narrow (unlike `showOnlyAssignedLoadTypes`, which covers any active dock).
   */
  docks?: Array<Pick<DockDetails, 'loadTypeIds'>>;
  /** Selects many load types, with select-all and a counted selection summary. */
  multiple?: boolean;
  /** Lists org-level load types under their own group. */
  includeOrgLoadTypes?: boolean;
  /** Restricts the list to load types assigned to one of the warehouse's docks. */
  showOnlyAssignedLoadTypes?: boolean;
  /** Offers a "create load type" row that navigates to the org's Load Types page. */
  allowCreate?: boolean;
  /**
   * Overrides the `canCreateLoadType` permission. Nullable because Vue casts an absent boolean
   * prop to `false`, which would shadow the permission.
   */
  canCreate?: boolean | null;
}>();

const emit = defineEmits<{
  /** Fired before the "create load type" row navigates away, so a parent dialog can close. */
  close: [];
  'update:model-value': [loadType: EntitySelectModelValue<LoadTypeDetails>];
}>();

const attrs = useAttrs();
const { t } = useI18n();
const { rolePermissions } = useAuth();
const { orgRef } = useOrg();

const {
  data: loadTypes,
  error,
  isLoading,
  onIntersect
} = useLoadTypeSelectList({
  warehouseId: () => warehouse.id,
  includeOrgLoadTypes: () => includeOrgLoadTypes,
  showOnlyAssignedLoadTypes: () => showOnlyAssignedLoadTypes,
  selectedLoadType: () => (Array.isArray(modelValue) ? modelValue[0] : modelValue)
});

const {
  entries,
  getDirection,
  getDirectionIcon,
  getDirectionIconColor,
  getDuration,
  getLabel,
  getName,
  visibleLoadTypes
} = useLoadTypeSelectItems({
  loadTypes,
  warehouseName: () => warehouse.name,
  docks: () => docks
});

const {
  autocompleteModelValue,
  getItemIcon,
  onSelectionUpdate,
  selectAllIcon,
  selectionSummary,
  selectOnly,
  toggleSelectAll
} = useEntitySelectModel<LoadTypeDetails>({
  modelValue: () => modelValue,
  items: visibleLoadTypes,
  multiple: () => multiple,
  getItemTitle: getLabel,
  getCountLabel: count => t('global.specialtyTerms.loadTypes', count),
  onUpdate: value => emit('update:model-value', value)
});

/**
 * Only picks the empty-state message: Vuetify owns filtering and echoes the selected title
 * back through this event, so it must never narrow the rows.
 */
const searchInput = ref('');

const noDataMessage = computed(() => {
  if (error.value) {
    return t('forms.elements.selects.loadTypeSelect.messages.loadFailed');
  }

  if (searchInput.value) {
    return t('forms.elements.selects.loadTypeSelect.messages.noMatchingLoadTypesFound');
  }

  return t('forms.elements.selects.loadTypeSelect.messages.looksLikeWarehouseDoesntHaveLoadType');
});

/** Empty for headers and dividers so they stay out of the input and Vuetify's filter drops them. */
const getItemTitle = (entry: LoadTypeSelectEntry) =>
  isSelectGroupItem(entry) ? getLabel(entry) : '';

const showSelectAllRow = computed(() => multiple && visibleLoadTypes.value.length > 0);

/** Needs the org loaded, since the row links into `/org/{orgId}/loadtypes`. */
const showCreateRow = computed(
  () =>
    allowCreate &&
    (canCreate ?? Boolean(rolePermissions.value.canCreateLoadType)) &&
    Boolean(orgRef.value)
);

/** Matches the legacy empty-state link: the Load Types page with its create form open. */
const createLoadTypeRoute = computed(() => ({
  path: `/org/${orgRef.value?.id}/loadtypes`,
  query: { showLoadTypeForm: 'true' }
}));

/** Lets a parent dialog close itself before the router takes the user to a new page. */
const navigateToCreateLoadType = (navigate: () => void) => {
  emit('close');
  navigate();
};
</script>

<template>
  <v-autocomplete
    return-object
    color="primary"
    data-testid="load-type-select-v2"
    density="compact"
    hide-details="auto"
    item-value="id"
    v-bind="attrs"
    v-intersect="{ handler: onIntersect }"
    variant="outlined"
    :item-title="getItemTitle"
    :items="entries"
    :loading="isLoading"
    :model-value="autocompleteModelValue"
    :multiple="multiple"
    :placeholder="t('forms.elements.selects.loadTypeSelect.placeholder', multiple ? 2 : 1)"
    @update:model-value="onSelectionUpdate"
    @update:search="value => (searchInput = String(value ?? '').trim())">
    <template v-if="showSelectAllRow || $slots['prepend-item']" #prepend-item>
      <v-list-item
        v-if="showSelectAllRow"
        ripple
        class="typography-body-md"
        data-testid="load-type-select-select-all"
        :title="t('forms.common.actions.selectAll')"
        @click="toggleSelectAll">
        <template #prepend>
          <v-icon :icon="selectAllIcon" />
        </template>
      </v-list-item>

      <slot name="prepend-item" />

      <v-divider v-if="showSelectAllRow" class="mt-2" />
    </template>

    <template #selection="{ item, index }">
      <span v-if="!multiple" class="typography-body-md" data-testid="load-type-select-selection">
        {{ getItemTitle(item.raw) }}
      </span>
      <span
        v-else-if="index === 0"
        class="typography-body-md d-inline-flex align-center ga-1"
        data-testid="load-type-select-selection">
        {{ selectionSummary.text }}
        <v-badge
          v-if="selectionSummary.count !== null"
          inline
          color="neutral-80"
          :content="selectionSummary.count" />
      </span>
    </template>

    <template #item="{ item, props: itemProps }">
      <v-list-subheader v-if="isSelectGroupHeader(item.raw)">
        <span class="typography-caption-bold text-uppercase text--color-neutral-60">
          {{ item.raw.header }}
        </span>
      </v-list-subheader>
      <v-divider v-else-if="isSelectGroupDivider(item.raw)" />
      <v-list-item v-else ripple v-bind="itemProps" title="">
        <template v-if="multiple" #prepend>
          <v-icon :icon="getItemIcon(item.raw)" />
        </template>

        <template #title>
          <span class="typography-body-md">
            {{ getName(item.raw) }}
          </span>
        </template>

        <template v-if="getDirection(item.raw) || getDuration(item.raw)" #subtitle>
          <span
            data-testid="load-type-select-details"
            class="d-flex flex-row ga-1 align-center typography-body-sm text--color-text-secondary">
            <template v-if="item.raw.direction">
              <v-icon
                size="16"
                :color="getDirectionIconColor(item.raw.direction)"
                :icon="getDirectionIcon(item.raw.direction)" />
              <span>{{ getDirection(item.raw) }}</span>
            </template>
            <span v-if="getDirection(item.raw) && getDuration(item.raw)">&bull;</span>
            <span v-if="getDuration(item.raw)">{{ getDuration(item.raw) }}</span>
          </span>
        </template>

        <template v-if="multiple" #append>
          <v-btn
            class="text-none typography-button-sm ml-auto"
            size="small"
            variant="text"
            data-testid="load-type-select-only"
            @click="selectOnly($event, item.raw)">
            {{ t('global.basicTerms.only') }}
          </v-btn>
        </template>
      </v-list-item>
    </template>

    <template #no-data>
      <v-list-item>
        <v-list-item-title>
          {{ noDataMessage }}
        </v-list-item-title>
      </v-list-item>
    </template>

    <template v-if="showCreateRow || $slots['append-item']" #append-item>
      <v-divider class="mt-2" />

      <slot name="append-item" />

      <router-link v-if="showCreateRow" custom v-slot="{ navigate }" :to="createLoadTypeRoute">
        <v-list-item
          data-testid="load-type-select-create-new-item"
          :disabled="isLoading"
          @click="navigateToCreateLoadType(navigate)">
          <v-list-item-title>
            <span class="d-flex flex-row ga-1 align-center typography-body-md">
              <span>
                {{ t('forms.elements.selects.loadTypeSelect.messages.didntFindLoadType') }}
              </span>
              <span class="typography-body-md-bold">
                {{ t('forms.elements.selects.loadTypeSelect.buttons.createNew') }}
              </span>
            </span>
          </v-list-item-title>
        </v-list-item>
      </router-link>
    </template>
  </v-autocomplete>
</template>
